// Formatting helpers shared by the route profile views.

export function formatDistance(meters) {
  if (meters == null || meters <= 0) return '--';
  const km = meters / 1000;
  return km >= 100 ? `${Math.round(km)} km` : `${km.toFixed(1)} km`;
}

export function formatDuration(sec) {
  if (sec == null || sec <= 0) return '--';
  const h = Math.floor(sec / 3600);
  const m = Math.round((sec % 3600) / 60);
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}min`;
  return `${m}min`;
}

export function formatElevation(m) {
  if (m == null) return '--';
  return `${Math.round(m)} m`;
}

export function formatGrade(pct) {
  if (pct == null) return '--';
  return `${pct.toFixed(1)}%`;
}

// Human label for a climb category code.
export function climbCategoryLabel(category, t) {
  if (!category || category === 'S/C') return t('map.profile.climbUncat');
  if (category === 'HC') return t('map.profile.climbHC');
  return t('map.profile.climbCat', { cat: category });
}

// Color scale for the climb category badge.
export function climbCategoryColor(category) {
  switch (category) {
    case 'HC':
      return '#9b2c2c';
    case '1':
      return '#c05621';
    case '2':
      return '#b7791f';
    case '3':
      return '#2f855a';
    case '4':
      return '#2c7a7b';
    default:
      return '#4a5568';
  }
}

const pad = (n) => String(n).padStart(2, '0');

/** Formats a Date as "HH:mm". */
export function formatHHMM(date) {
  if (!(date instanceof Date) || isNaN(date)) return '';
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Formats a Date as a short local date string, e.g. "7 jun". */
export function formatShortDate(date, lang = 'es') {
  if (!(date instanceof Date) || isNaN(date)) return '';
  return date.toLocaleDateString(lang === 'en' ? 'en-GB' : 'es-ES', {
    day: 'numeric',
    month: 'short',
  });
}

/**
 * Returns each segment enriched with `arrivalTime` (Date) — the moment the
 * traveler reaches the END of that segment, calculated from departureTime by
 * accumulating durationSec values.
 */
export function segmentArrivalTimes(segments, departureTime) {
  if (!Array.isArray(segments) || !departureTime) return segments || [];
  const dep =
    departureTime instanceof Date && !isNaN(departureTime)
      ? departureTime
      : new Date();
  let elapsed = 0;
  let cumDist = 0;
  return segments.map((seg) => {
    const arrivalTime = new Date(dep.getTime() + elapsed * 1000);
    const cumulativeDist = cumDist;
    elapsed += seg.durationSec || 0;
    cumDist += seg.distance || 0;
    return { ...seg, arrivalTime, cumulativeDist };
  });
}

/**
 * Picks up to `count` representative segments distributed uniformly across the
 * full segment list (first, last, and evenly-spaced in between).
 */
export function pickRepresentativeSegments(segments, count = 5) {
  if (!segments || segments.length === 0) return [];
  if (segments.length <= count) return segments;
  const result = [];
  for (let i = 0; i < count; i++) {
    const idx = Math.round((i / (count - 1)) * (segments.length - 1));
    result.push(segments[idx]);
  }
  return result;
}

/** Human-readable label for travel mode. */
export function travelModeLabel(mode, lang = 'es') {
  const labels = {
    es: { BICYCLING: 'Bicicleta', DRIVING: 'Coche', WALKING: 'A pie', TRANSIT: 'Transporte' },
    en: { BICYCLING: 'Bicycle', DRIVING: 'Car', WALKING: 'Walking', TRANSIT: 'Transit' },
  };
  return (labels[lang] || labels.es)[mode] || mode || '';
}

// Builds an SVG path "d" for the elevation area and the top line from samples.
// Returns { area, line, width, height } in the given pixel box.
export function buildElevationPaths(samples, width, height, padTop = 6) {
  if (!samples || samples.length < 2) return null;
  const eles = samples.map((s) => s.ele);
  const minEle = Math.min(...eles);
  const maxEle = Math.max(...eles);
  const range = maxEle - minEle || 1;
  const maxDist = samples[samples.length - 1].dist || 1;
  const bottom = height;

  const x = (d) => (d / maxDist) * width;
  const y = (e) => padTop + (1 - (e - minEle) / range) * (height - padTop);

  let line = '';
  samples.forEach((s, i) => {
    line += `${i === 0 ? 'M' : 'L'}${x(s.dist).toFixed(1)},${y(s.ele).toFixed(1)} `;
  });
  const area =
    `M${x(0).toFixed(1)},${bottom} ` +
    samples.map((s) => `L${x(s.dist).toFixed(1)},${y(s.ele).toFixed(1)}`).join(' ') +
    ` L${x(maxDist).toFixed(1)},${bottom} Z`;

  return { area, line, minEle, maxEle, maxDist };
}
